import {
  BookOpen,
  LifeBuoy,
  LogOut,
  MapPinned,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
} from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { api, type AuthStatus, type Me } from "./lib/api.js";
import { logout } from "./lib/passkeys.js";
import { Spinner } from "./lib/Spinner.js";
import { Dashboard } from "./pages/Dashboard.js";
import { Enroll } from "./pages/Enroll.js";
import { Guide } from "./pages/Guide.js";
import { KidDecrypt } from "./pages/KidDecrypt.js";
import { Login } from "./pages/Login.js";
import { NewVault } from "./pages/NewVault.js";
import { Privacy } from "./pages/Privacy.js";
import { Settings } from "./pages/Settings.js";
import { Setup } from "./pages/Setup.js";
import { Timeline } from "./pages/Timeline.js";
import { TimelineHome } from "./pages/TimelineHome.js";
import { Unlock } from "./pages/Unlock.js";
import { UnlockRequest } from "./pages/UnlockRequest.js";
import { Vault } from "./pages/Vault.js";

export interface Session {
  status: AuthStatus | null;
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionCtx = createContext<Session>({
  status: null,
  me: null,
  loading: true,
  refresh: async () => undefined,
  signOut: async () => undefined,
});
export const useSession = () => useContext(SessionCtx);

function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    const s = await api<AuthStatus>("/v1/auth/status");
    setStatus(s);
    setMe(s.user ? await api<Me>("/v1/me") : null);
    setLoading(false);
  }, []);
  useEffect(() => {
    refresh().catch(() => setLoading(false));
  }, [refresh]);
  const signOut = useCallback(async () => {
    await logout();
    await refresh();
  }, [refresh]);
  return (
    <SessionCtx.Provider value={{ status, me, loading, refresh, signOut }}>
      {children}
    </SessionCtx.Provider>
  );
}

function Shell({ children }: { children: ReactNode }) {
  const { me, signOut } = useSession();
  return (
    <div className="shell">
      <div className="topbar">
        <Link to="/" className="brand">
          <span className="brand-mark">
            <Shield size={16} strokeWidth={2.5} />
          </span>
          Te<span>th</span>er
        </Link>
        <div className="nav">
          {!me && (
            <NavLink to="/guide">
              <BookOpen size={16} className="icon" />
              Guide
            </NavLink>
          )}
          {me ? (
            <>
              <NavLink to="/">
                <ShieldCheck size={16} className="icon" />
                Vaults
              </NavLink>
              <NavLink to="/timeline">
                <MapPinned size={16} className="icon" />
                Timeline
              </NavLink>
              <NavLink to="/unlock">
                <LifeBuoy size={16} className="icon" />
                Emergency unlock
              </NavLink>
              <NavLink to="/settings">
                <SlidersHorizontal size={16} className="icon" />
                Settings
              </NavLink>
              <NavLink to="/guide">
                <BookOpen size={16} className="icon" />
                Guide
              </NavLink>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  void signOut();
                }}
              >
                <LogOut size={16} className="icon" />
                Sign out ({me.user.name})
              </a>
            </>
          ) : null}
        </div>
      </div>
      {children}
      <footer className="site-footer">
        <Link to="/privacy">Privacy</Link>
      </footer>
    </div>
  );
}

function Gate({ children }: { children: ReactNode }) {
  const { status, me, loading } = useSession();
  const loc = useLocation();
  if (loading) return <Spinner label="Loading…" />;
  if (!status) return <p className="error">The gateway is not reachable.</p>;
  const setupNeeded = !status.setup.storageConfigured || !status.setup.hasOwner;
  if (loc.pathname.startsWith("/guide") || loc.pathname.startsWith("/privacy"))
    return <>{children}</>;
  if (setupNeeded && !loc.pathname.startsWith("/setup") && !loc.pathname.startsWith("/enroll"))
    return <Navigate to="/setup" replace />;
  if (
    !setupNeeded &&
    !me &&
    !loc.pathname.startsWith("/login") &&
    !loc.pathname.startsWith("/enroll")
  )
    return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <SessionProvider>
      <Shell>
        <Gate>
          <Routes>
            <Route path="/setup" element={<Setup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/enroll/:token" element={<Enroll />} />
            <Route path="/" element={<Dashboard />} />
            <Route path="/vaults/new" element={<NewVault />} />
            <Route path="/vaults/:vaultId" element={<Vault />} />
            <Route path="/vaults/:vaultId/timeline" element={<Timeline />} />
            <Route path="/vaults/:vaultId/decrypt" element={<KidDecrypt />} />
            <Route path="/timeline" element={<TimelineHome />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/guide" element={<Guide />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/unlock" element={<Unlock />} />
            <Route path="/unlock/:id" element={<UnlockRequest />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Gate>
      </Shell>
    </SessionProvider>
  );
}
